import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Select,
  Switch,
  Input,
  message,
  Popconfirm,
  Tag,
} from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { showIssues } from "../../../helper/notify";
import { validateMasterSuiteSteps } from "../../../helper/masterSuiteValidation";
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  subCategories,
  processSubCategories,
  categories,
} from "../../../constants/List Constants";
import SelectWithAdd from "../../../components/generic/SelectWithAdd";
import "../../../styles/custom-dropdown.css";
import "../../../styles/custom-switch.css";
import VariablesModal from "../variables/VariablesModal";
import TaskModal from "../../task/TaskModal";
import {
  ancestorsOf,
  healSelfParents,
  relinkOnDelete,
  relinkOnRename,
} from "../../../helper/masterSuiteTree";
import {
  changeCategoryValue,
  changeSubCategoryValue,
} from "../../../helper/string_helper";

const { Option } = Select;
const RowContext = React.createContext({});

// UI Automation subcategory option (only one) + all categories incl. UI — keeps
// this Edit table consistent with the New flow's dropdowns.
const uiSubCategories = [{ label: "UI Test", value: "uiTest" }];
const allCategories = [
  ...(categories || []),
  { label: "UI Automation", value: "ui" },
];
const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };
  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners]
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};
const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{
        cursor: "move",
      }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

function MasterSuiteCopyTable(props) {
  const [subCategoryEnable, setSubCategoryEnable] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [count, setCount] = useState(0);
  const [errorFields, setErrorFields] = useState({});
  const [variablesVisible, setVariablesVisible] = useState(false);
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [taskNames, setTaskNames] = useState([]);
  const [categoryValue, setCategoryValue] = useState("");
  const [subCategoryValue, setSubCategoryValue] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [subCategoryKey, setSubCategoryKey] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [stepNames, setStepNames] = useState([]);
  const [defaultEnvironment, setDefaultEnvironment] = useState("");
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [currentKey, setCurrentKey] = useState("");
  useEffect(
    () => {
      if (props.existingData) {
        // healSelfParents on load: a suite saved before the rename guard existed can carry
        // a step whose parent is its own name. Nothing else repairs it — the canvas only
        // tolerates it — and it blocks Save, so fix it the moment the suite is opened.
        setDataSource(
          healSelfParents(
            props.existingData.map((item, index) => ({
              ...item,
              key: item.key || index, // Use existing key or assign index-based fallback
            })),
          )
        );
        setCount(props.existingData.length + 1);
      }
    },
    [props.existingData],
    [count]
  );
  useEffect(() => {
    if (props.newlyCreatedTask) {
      setDataSource((prevDataSource) =>
        prevDataSource.map((item) => {
          if (item.key === selectedRecord.key) {
            return {
              ...item,
              filteredTaskArray: [
                ...(item.filteredTaskArray || []),
                props.newlyCreatedTask,
              ],
              taskName: props.newlyCreatedTask.taskName,
            };
          }
          return item;
        })
      );
      setTaskNames((prevTaskNames) => [
        ...new Set([...prevTaskNames, props.newlyCreatedTask.taskName]),
      ]);
      props.resetNewlyCreatedTask();
    }
    setEnvironments(props.environments);
    setDefaultEnvironment(props.defaultEnvironment);
    props.onSave(dataSource);
  }, [dataSource, props, props.newlyCreatedTask, selectedRecord]);
  const getUniqueTaskNames = () => {
    const taskNames = props.suiteArray.map((suite) => suite.taskName);
    return [...new Set(taskNames)];
  };
  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.key === active?.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.key === over?.id
        );
        const reorderedData = arrayMove(prevState, activeIndex, overIndex).map(
          (item, index) => ({
            ...item,
            sequence: index + 1,
          })
        );
        return reorderedData;
      });
    }
  };
  const updateDataSource = (key, value, record) => {
    const updatedData = dataSource.map((item) =>
      item.key === record.key ? { ...item, [key]: value } : item
    );
    setDataSource(updatedData);
  };
  const updateStepNamesArray = (updatedSteps) => {
    const updatedStepNames = updatedSteps.map((item) => item.stepReference);
    setStepNames(updatedStepNames);
  };
  const handleAdd = () => {
    const { errorFields, issues, hasErrors } =
      validateMasterSuiteSteps(dataSource);
    if (hasErrors) {
      setErrorFields(errorFields);
      showIssues("Can't add a step yet", issues);
      return;
    }
    setSubCategoryEnable(true);
    const newData = {
      key: count,
      sequence: dataSource.length + 1,
      type: null,
      category: null,
      subcategory: null,
      stepReference: null,
      taskName: null,
      variables: [],
      stopIfFailed: false,
    };
    setDataSource([...dataSource, newData]);
    setCount(count + 1);
    const updatedSteps = [...dataSource, newData];
    updateStepNamesArray(updatedSteps);
  };
  const handleDelete = (key) => {
    // Filter out the deleted item based on key (which is now sequence)
    const removed = dataSource.find((item) => item.key === key);
    const updatedDataSource = dataSource.filter((item) => item.key !== key);
    // Splice the deleted step out of the tree so its children adopt its parent — without
    // this, deleting a step in a branched suite leaves its children pointing at a step
    // that no longer exists, and they silently become roots.
    const relinked = relinkOnDelete(updatedDataSource, removed);
    // Re-index the remaining data after deletion
    const reIndexedData = relinked.map((item, index) => ({
      ...item,
      sequence: index + 1, // Adjust sequence number after deletion
    }));
    // Update state with the new data
    setDataSource(reIndexedData);
    updateStepNamesArray(reIndexedData);
  };
  const handleTypeChange = (value, record) => {
    const updatedData = dataSource.map((item) =>
      item.key === record.key
        ? {
            ...item,
            type: value,
            category: value === "masterSuite" ? "N/A" : null,
            subcategory: value === "masterSuite" ? "N/A" : null,
            variables: {},
            filteredTaskArray: [],
            taskName: null,
          }
        : item
    );
    setDataSource(updatedData);
  };
  const handleCategoryChange = (value, record) => {
    let subCategoryOptions = [];
    let categoryLabel = changeCategoryValue(value);
    switch (value) {
      case "compare":
        setSubCategoryEnable(false);
        subCategoryOptions = subCategories;
        break;
      case "process":
        setSubCategoryEnable(false);
        subCategoryOptions = processSubCategories;
        break;
      case "ui":
        subCategoryOptions = uiSubCategories;
        break;
      default:
        subCategoryOptions = []; // Empty if no subcategory is relevant
        break;
    }
    const isUi = value === "ui";
    const updatedData = dataSource.map((item) =>
      item.key === record.key
        ? {
            ...item,
            category: value,
            // UI auto-selects its sub-category and lists UI suites instead of tasks.
            subcategory: isUi ? "uiTest" : null,
            subCategoryValue: isUi ? changeSubCategoryValue("uiTest") : undefined,
            subCategoryOptions,
            categoryValue: categoryLabel,
            filteredTaskArray: isUi
              ? (props.uiSuiteList || []).map((s) => ({
                  taskName: s.suiteName,
                  taskId: s.suiteId,
                  isUiSuite: true,
                }))
              : [],
            taskName: null,
          }
        : item
    );
    setDataSource(updatedData);
  };
  const handleSubcategoryChange = (value, record) => {
    setSelectedVariables([]);
    let subCategoryValue = changeSubCategoryValue(value);
    const updatedData = dataSource.map((item) => {
      if (item.key === record.key) {
        let filteredTaskArray = [];
        if (value === "uiTest") {
          filteredTaskArray = (props.uiSuiteList || []).map((s) => ({
            taskName: s.suiteName,
            taskId: s.suiteId,
            isUiSuite: true,
          }));
        } else if (value === "file2spec") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "File to Specification"
          );
          ///setFilteredTaskList(filteredTaskArray);
        } else if (value === "file2Db") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "File to Database"
          );
          //setFilteredTaskList(filteredTaskArray);
        } else if (value === "file2File") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "File to File"
          );
          //setFilteredTaskList(filteredTaskArray);
        } else if (value === "db2Db") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "Database to Database"
          );
        } else if (value === "eventMonitor") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "Event Monitor"
          );
        } else if (value === "move") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "File Move, Copy or Delete"
          );
        } else if (value === "extractCompressed") {
          filteredTaskArray = props.suiteArray.filter(
            (suite) => suite.taskSubCategory === "Extract Compressed File"
          );
        }
        return {
          ...item,
          subcategory: value,
          filteredTaskArray,
          subCategoryValue: subCategoryValue,
          taskName: null,
        };
      }
      return item;
    });
    setDataSource(updatedData);
  };
  const handleStepReferenceChange = (value, record) => {
    const isDuplicate = dataSource.some(
      (item) => item.stepReference === value && item.key !== record.key
    );
    setErrorFields((prevState) => ({
      ...prevState,
      [record.key]: {
        stepReference: isDuplicate ? "Step name already exists" : undefined,
      },
    }));
    const updatedSteps = dataSource.map((item) => {
      if (item.key === record.key) {
        const updatedItem = { ...item, stepReference: value };
        if (item.variables && typeof item.variables === "object") {
          updatedItem.variables = {
            ...item.variables,
            stepReference: value,
            inputFromOne:
              updatedItem.variables.inputFromOne === item.stepReference
                ? value
                : updatedItem.variables.inputFromOne,
            inputFromTwo:
              updatedItem.variables.inputFromTwo === item.stepReference
                ? value
                : updatedItem.variables.inputFromTwo,
          };
        }
        return updatedItem;
      }
      return item;
    });
    const finalUpdatedSteps = updatedSteps.map((item) => {
      if (item.variables && typeof item.variables === "object") {
        item.variables = {
          ...item.variables,
          inputFromOne:
            item.variables.inputFromOne === record.stepReference
              ? value
              : item.variables.inputFromOne,
          inputFromTwo:
            item.variables.inputFromTwo === record.stepReference
              ? value
              : item.variables.inputFromTwo,
        };
      }
      return item;
    });
    // Children follow the rename. Without this, editing a branched suite and renaming a
    // step orphans everything below it — they become roots and start immediately. The key
    // stops the renamed step re-pointing its OWN parent at itself when its name briefly
    // matches its parent's. No heal here: see the note in useMasterSuiteStepEditor —
    // healing mid-word would null a good parent link that never returns.
    const relinked = relinkOnRename(
      finalUpdatedSteps,
      record.stepReference,
      value,
      record.key,
    );
    setDataSource(relinked);
    updateStepNamesArray(relinked);
  };
  const handleTaskNameChange = (value, record) => {
    updateDataSource("taskName", value, record);
    setSelectedRecord(record);
  };
  const handleVariablesClick = (record) => {
    if (record.category === "ui") {
      message.info(
        "UI Automation steps do not require variable configuration."
      );
      return;
    }
    if (!props.defaultEnvironment?.length) {
      message.error(
        "Please select default environment to proceed with configuring variables."
      );
      return;
    }
    if (record.type === "masterSuite") {
      message.error(
        "Master Suite cannot have any variables at this point. Please select task"
      );
      return;
    }
    if (record.taskName === null || record.taskName === "") {
      message.error(
        "Please select a task to proceed with configuring variables."
      );
      return;
    }
    const subCategoryValue = record.subcategory || "";
    const subCategoryLabel = changeSubCategoryValue(subCategoryValue);
    console.log(record.variables);
    setCurrentKey(record.key);
    setSubCategoryValue(subCategoryLabel);
    setSubCategoryKey(subCategoryValue);
    setSelectedRecord(record);
    setSelectedVariables(record.variables || []);
    setVariablesVisible(true);
  };
  const handleVariablesOnClose = (visible) => {
    setVariablesVisible(visible);
  };
  const handleSaveVariables = (setVariables, record) => {
    const variables = {
      ...setVariables,
      categoryValue: record.categoryValue,
      subCategoryValue: record.subCategoryValue,
      stepReference: record.stepReference,
      defaultEnvironmentOne:
        setVariables.environmentOne === props.defaultEnvironment ? true : false,
      defaultEnvironmentTwo:
        setVariables.environmentTwo === props.defaultEnvironment ? true : false,
    };
    const updatedDataSource = dataSource.map((item) =>
      item.key === record.key ? { ...item, variables } : item
    );
    console.log(updatedDataSource);
    setDataSource(updatedDataSource);
    setVariablesVisible(false);
  };
  const handleAddTaskItem = (record) => {
    if (record.category === "ui") {
      message.info(
        "Create new UI automation suites from the UI Automation page."
      );
      return;
    }
    const categoryValue = record.categoryValue || "";
    const categoryLabel = changeCategoryValue(record.category);
    const subCategoryValue = record.subcategory || "";
    const subCategoryLabel = changeSubCategoryValue(subCategoryValue);
    setCategoryValue(categoryValue);
    setSubCategoryValue(subCategoryLabel);
    setCategoryKey(categoryLabel);
    setSubCategoryKey(subCategoryValue);
    setSelectedRecord(record);
    setTaskModalVisible(!taskModalVisible);
  };
  const handleSwitchChange = (checked, record) => {
    const updatedData = dataSource.map((item) =>
      item.key === record.key ? { ...item, stopIfFailed: checked } : item
    );
    setDataSource(updatedData);
  };
  const handleTaskCreated = (newTask) => {
    setDataSource((prevDataSource) =>
      prevDataSource.map((item) => {
        if (item.key === selectedRecord.key) {
          return {
            ...item,
            filteredTaskArray: [...(item.filteredTaskArray || []), newTask],
            taskName: newTask.taskName,
          };
        }
        return item;
      })
    );
    setTaskNames((prevTaskNames) => [
      ...new Set([...prevTaskNames, newTask.taskName]),
    ]);
    props.refreshTaskData(newTask);
    setTaskModalVisible(false);
  };
  const columns = [
    {
      key: "sort",
      align: "center",
      render: () => <DragHandle />,
      width: "5%",
    },
    {
      title: "",
      dataIndex: "add",
      key: "add",
      width: "4%",
      render: (_, record) => (
        <Popconfirm
          title="Are you sure?"
          onConfirm={() => handleDelete(record.key)}
        >
          <DeleteOutlined style={{ color: "red" }} />
        </Popconfirm>
      ),
    },
    {
      title: "",
      dataIndex: "sequence",
      key: "sequence",
      width: "2%",
      align: "center",
      render: (_, record) => record.sequence,
    },
    {
      title: "Step Name",
      dataIndex: "stepReference",
      key: "stepReference",
      render: (stepReference, record) => {
        const hasError = errorFields[record.key]?.stepReference;
        return (
          <>
            <Input
              value={stepReference}
              style={{
                fontSize: "12px",
                borderColor: hasError ? "red" : undefined,
              }}
              status={hasError ? "error" : undefined}
              onChange={(e) =>
                handleStepReferenceChange(e.target.value, record)
              }
              placeholder={hasError ? hasError : "Enter Suite Reference"}
              maxLength={50}
            />
          </>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      responsive: ["md"],
      render: (_, record) => (
        <Select
          style={{
            width: "100%",
            borderColor: errorFields[record.key]?.type ? "red" : undefined,
          }}
          className="custom-select"
          onChange={(value) => handleTypeChange(value, record)}
          placeholder="Select Type"
          value={record.type || undefined}
        >
          <Option value="masterSuite">Master Suite</Option>
          <Option value="task">Task</Option>
        </Select>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: "15%",
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          className="custom-select"
          onChange={(value) => handleCategoryChange(value, record)}
          value={record.type === "masterSuite" ? "N/A" : record.category}
          placeholder="Select Category"
          options={allCategories}
          disabled={!record.type || record.type === "masterSuite"}
        />
      ),
    },
    {
      title: "Sub Category",
      dataIndex: "subcategory",
      key: "subcategory",
      responsive: ["lg"],
      render: (_, record) => {
        // UI auto-selects its sub-category — show as a read-only tag (like New).
        if (record.category === "ui") {
          return (
            <Tag color="purple" style={{ margin: 0 }}>
              UI Test
            </Tag>
          );
        }
        return (
          <Select
            style={{ width: "100%" }}
            className="custom-select"
            value={record.type === "masterSuite" ? "N/A" : record.subcategory}
            onChange={(value) => handleSubcategoryChange(value, record)}
            placeholder="Select Sub Category"
            options={record.subCategoryOptions || []}
            disabled={subCategoryEnable}
          />
        );
      },
    },
    {
      title: "Task/Suite Name",
      dataIndex: "taskName",
      key: "taskName",
      width: "20%",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <SelectWithAdd
            placeholder={
              record.category === "ui" ? "select UI suite" : "suite name"
            }
            options={record.filteredTaskArray || []}
            value={record.taskName || undefined}
            onSelect={(value) => handleTaskNameChange(value, record)}
            labelKey="taskName"
            valueKey="taskName"
            addItem={() => handleAddTaskItem(record)}
            buttonDisabled={
              !record.type ||
              record.type === "masterSuite" ||
              record.category === "ui"
            }
            disabled={!record.subcategory}
          />
        </div>
      ),
    },
    {
      title: "Parameters",
      dataIndex: "variables",
      key: "variables",
      align: "center",
      responsive: ["md"],
      render: (_, record) => {
        // UI Automation steps have no parameters.
        if (record.category === "ui") {
          return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
        }
        return (
          <Button
            size="small"
            style={{ fontSize: "12px", marginLeft: "5px" }}
            type="link"
            onClick={() => handleVariablesClick(record)}
          >
            {record.variables && Object.keys(record.variables).length > 0
              ? "Edit"
              : "Add"}
          </Button>
        );
      },
      width: "10%",
    },
    {
      title: "Stop if Failed",
      dataIndex: "stopIfFailed",
      key: "stopIfFailed",
      responsive: ["md"],
      render: (_, record) => (
        <Switch
          className="custom-switch"
          checked={record.stopIfFailed}
          onChange={(checked) => handleSwitchChange(checked, record)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      ),
      align: "center",
    },
  ];
  return (
    <>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map((i) => i.key) || []}
          strategy={verticalListSortingStrategy}
        >
          <Table
            className="custom-table-shadow"
            columns={columns}
            dataSource={dataSource}
            rowKey="key"
            components={{
              body: {
                row: Row,
              },
            }}
            scroll={{ y: 400 }}
            /* steps are one ordered list — paging them would break drag-reorder */
            pagination={false}
          />
        </SortableContext>
      </DndContext>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        className="db-add"
        style={{
          marginTop: 16,
          float: "left",
        }}
      >
        Add Step
      </Button>
      <VariablesModal
        open={variablesVisible}
        onClose={handleVariablesOnClose}
        environments={environments}
        subCategoryValues={subCategoryValue}
        // Only this step's ANCESTORS — a sibling branch runs at the same time, so its
        // output may not exist yet. Narrower than before for linear suites too: the old
        // list also offered LATER steps, which could never have resolved at run time.
        stepNames={ancestorsOf(dataSource, { key: currentKey })
          .filter((item) => item.category !== "compare")
          .map((item) => item.stepReference)}
        defaultEnvironment={props.defaultEnvironment}
        onSave={(variables) => handleSaveVariables(variables, selectedRecord)}
        variables={selectedVariables}
        folderPaths={props.folderPaths}
      />
      <TaskModal
        isOpen={taskModalVisible}
        openModal={handleAddTaskItem}
        suiteData={props.testSuiteList}
        taskNames={getUniqueTaskNames(props.testSuiteList)}
        refreshTaskData={props.refreshTaskData}
        disabledCategory={true}
        disabledSubCategory={true}
        categoryValue={categoryValue}
        categoryKey={categoryKey}
        subCategoryValue={subCategoryValue}
        subCategoryKey={subCategoryKey}
        updateTaskData={props.updateTaskData}
        onTaskCreated={handleTaskCreated}
      />
    </>
  );
}

export default MasterSuiteCopyTable;
